import { useMemo, useRef } from 'react';
import { useInfiniteQuery, InfiniteData, useQueryClient, UseInfiniteQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { QUERY_KEYS } from '@restworld/utils-common';
import { useOrganizationsService, GetMissedOrgApplicationActivities } from '@restworld/data-services';
import { atom, useSetAtom } from 'jotai';

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 10;

export type InfiniteActivitiesQueryResponse = {
  activities?: GetMissedOrgApplicationActivities[];
  hasNextPage?: boolean;
  query?: UseInfiniteQueryResult<
    InfiniteData<
      AxiosResponse<
        {
          data: GetMissedOrgApplicationActivities[];
          hits: number;
        },
        any
      >,
      unknown
    >,
    Error
  >;
  isLoading?: boolean;
};
export const infiniteActivitiesQueryResponseAtom = atom<InfiniteActivitiesQueryResponse>({});

function useMissedOrganisationApplicationActivitiesQuery({
  limit = DEFAULT_LIMIT,
}: {
  limit?: number;
  offset?: number;
} = {}): InfiniteActivitiesQueryResponse {
  const setInfiniteActivitiesQueryResponse = useSetAtom(infiniteActivitiesQueryResponseAtom);
  const organisationsService = useOrganizationsService();
  const nextPageRef = useRef<number>(undefined);
  const hasNextPageRef = useRef<boolean>(undefined);
  const queryKey = useMissedOrganisationApplicationActivitiesQueryKey();

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return organisationsService.getMissedOrgApplicationActivities({ limit, offset: pageParam * limit });
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.data.hits;
      const activitiesLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = activitiesLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
  });

  const activitiesInfo = useMemo(() => extractActivitiesInfo(query.data), [query.data]);
  const data = {
    activities: activitiesInfo.items,
    hasNextPage: hasNextPageRef.current,
    query,
    isLoading: query.isLoading,
  };
  setInfiniteActivitiesQueryResponse(data);
  return {
    ...data,
  };
}
export default useMissedOrganisationApplicationActivitiesQuery;

type ActivitiesInfo = {
  items: GetMissedOrgApplicationActivities[];
  hits: number;
};

type InfiniteActivities = InfiniteData<
  AxiosResponse<
    {
      data: GetMissedOrgApplicationActivities[];
      hits: number;
    },
    unknown
  >,
  unknown
>;

export function extractActivitiesInfo(data?: InfiniteActivities): ActivitiesInfo {
  if (!data || !data.pages) return { items: [], hits: 0 };
  const items = data?.pages.flatMap((page) => page.data.data);
  const hits = data?.pages[0].data.hits;

  return { items, hits };
}

export function useMissedOrganisationApplicationActivitiesQueryKey() {
  return [QUERY_KEYS.ORGANISATIONS.MISSED_APPLICATION_ACTIVITIES];
}

export function useMissedOrganisationApplicationActivities() {
  const queryKey = useMissedOrganisationApplicationActivitiesQueryKey();

  const activities = useQueryClient().getQueryData(queryKey) as InfiniteActivities;
  const activitiesInfo = useMemo(() => extractActivitiesInfo(activities), [activities]);

  return activitiesInfo;
}
